import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchMembershipCatalog,
  type MembershipCatalogSettings,
  type MembershipPlan,
} from './membershipService';
import {
  formatMembershipMoney,
  getGoldCompleteCopy,
  getUpgradePitch,
  getUpgradeTiers,
  type BillingInterval,
} from './planDetails';
import { useClientMembership } from './useClientMembership';
import { MembershipPlanModal } from './MembershipPlanModal';
import './MembershipPricingPage.css';

const PlanIcon: React.FC<{ theme: string }> = ({ theme }) => {
  if (theme === 'gold') {
    return (
      <div className={`mp-icon mp-icon--${theme}`} aria-hidden>
        <svg viewBox="0 0 56 56" width="32" height="32" fill="none">
          <path
            d="M28 8l3.2 6.5 7.2 1-5.2 5.1 1.3 7.2L28 24.4l-6.5 3.4 1.3-7.2-5.2-5.1 7.2-1L28 8z"
            fill="currentColor"
            opacity="0.95"
          />
          <path
            d="M16 26c0 11.5 12 19.5 12 19.5S40 37.5 40 26"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
  if (theme === 'silver') {
    return (
      <div className={`mp-icon mp-icon--${theme}`} aria-hidden>
        <svg viewBox="0 0 56 56" width="32" height="32" fill="none">
          <path
            d="M28 10l13 7.2v11.2c0 9.4-7.2 15.4-13 17.6-5.8-2.2-13-8.2-13-17.6V17.2L28 10z"
            fill="currentColor"
            opacity="0.18"
          />
          <path
            d="M28 12.5l11 6.1v10c0 8.2-6.3 13.5-11 15.5-4.7-2-11-7.3-11-15.5v-10l11-6.1z"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }
  return (
    <div className={`mp-icon mp-icon--${theme}`} aria-hidden>
      <svg viewBox="0 0 56 56" width="32" height="32" fill="none">
        <path
          d="M28 12.5l11 6.1v10c0 8.2-6.3 13.5-11 15.5-4.7-2-11-7.3-11-15.5v-10l11-6.1z"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export const MembershipUpgradePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const renewMode = searchParams.get('mode') === 'renew';
  const { membership, isLive, isExpired } = useClientMembership({
    syncFromServer: true,
  });

  const [billing, setBilling] = useState<BillingInterval>(
    membership.billing === 'annual' ? 'annual' : 'monthly'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveBadge, setSaveBadge] = useState('Save up to 29%');
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [hubSettings, setHubSettings] = useState<MembershipCatalogSettings | null>(
    null
  );
  const [selected, setSelected] = useState<MembershipPlan | null>(null);

  useEffect(() => {
    if (!isLive && !isExpired) {
      navigate('/membership', { replace: true });
    }
  }, [isLive, isExpired, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMembershipCatalog();
        if (cancelled) return;
        if (data.settings) {
          setSaveBadge(data.settings.annual_save_badge || saveBadge);
          setHubSettings(data.settings);
        }
        setPlans(data.plans || []);
      } catch {
        if (!cancelled) setError('Unable to load upgrade plans right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ordered = useMemo(() => {
    const order = { bronze: 1, silver: 2, gold: 3 } as const;
    return [...plans].sort((a, b) => order[a.tier] - order[b.tier]);
  }, [plans]);

  const currentTier = membership.tier;
  const upgradeTiers = getUpgradeTiers(currentTier);

  const visiblePlans = useMemo(() => {
    if (renewMode || isExpired) return ordered;
    return ordered.filter((p) =>
      upgradeTiers.includes(p.tier as 'silver' | 'gold')
    );
  }, [ordered, renewMode, isExpired, upgradeTiers]);

  const pitch = useMemo(() => {
    if (renewMode || isExpired) {
      return {
        title: 'Renew your membership',
        body: 'Pick a plan to restore member benefits and savings.',
        bullets: [] as string[],
      };
    }
    if (currentTier === 'gold') {
      return getGoldCompleteCopy({
        title: hubSettings?.hub_gold_title,
        body: hubSettings?.hub_gold_body,
      });
    }
    if (currentTier === 'bronze' || currentTier === 'silver') {
      return getUpgradePitch(
        currentTier,
        currentTier === 'bronze'
          ? {
              title: hubSettings?.hub_bronze_upgrade_title,
              body: hubSettings?.hub_bronze_upgrade_body,
              bullets: hubSettings?.hub_bronze_upgrade_bullets,
            }
          : {
              title: hubSettings?.hub_silver_upgrade_title,
              body: hubSettings?.hub_silver_upgrade_body,
              bullets: hubSettings?.hub_silver_upgrade_bullets,
            }
      );
    }
    return {
      title: 'Upgrade your plan',
      body: 'Choose a higher tier to unlock more support.',
      bullets: [] as string[],
    };
  }, [renewMode, isExpired, currentTier, hubSettings]);

  const goGetStarted = (plan: MembershipPlan) => {
    setSelected(null);
    navigate(
      `/membership/get-started?plan=${encodeURIComponent(plan.plan_id)}&billing=${billing}`
    );
  };

  if (loading) {
    return (
      <div className="mp-page">
        <div className="mp-atmosphere" aria-hidden />
        <div className="mp-loading">
          Loading upgrades
          <div className="mp-loading-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-page is-ready mp-page--upgrade">
      <div className="mp-atmosphere" aria-hidden />
      <div className="mp-shell">
        <button
          type="button"
          className="mp-back-link"
          onClick={() => navigate('/membership')}
        >
          ← Back to membership
        </button>

        <header className="mp-header">
          <h1 className="mp-title">
            {renewMode || isExpired ? 'Renew your plan' : 'Upgrade your plan'}
          </h1>
          <p className="mp-subtitle">{pitch.body}</p>
        </header>

        <section className="mp-pitch mp-pitch--compact">
          <h2 className="mp-pitch-title">{pitch.title}</h2>
          {pitch.bullets.length > 0 && (
            <ul className="mp-pitch-list">
              {pitch.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </section>

        {currentTier === 'gold' && !renewMode && !isExpired ? (
          <div className="mp-empty">
            <h2>You’re on our highest plan</h2>
            <p>{pitch.body}</p>
            <button
              type="button"
              className="mp-text-link"
              onClick={() => navigate('/membership')}
            >
              Return to membership
            </button>
          </div>
        ) : (
          <>
            <div className="mp-section-head">
              <div className="mp-billing-wrap">
                <div className="mp-billing" role="group" aria-label="Billing interval">
                  <button
                    type="button"
                    className={`mp-billing-btn${billing === 'monthly' ? ' is-active' : ''}`}
                    onClick={() => setBilling('monthly')}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    className={`mp-billing-btn${billing === 'annual' ? ' is-active' : ''}`}
                    onClick={() => setBilling('annual')}
                  >
                    Annual
                  </button>
                </div>
                <span className="mp-save-badge">{saveBadge}</span>
              </div>
            </div>

            {error && <div className="mp-error">{error}</div>}

            <div
              className={`mp-grid${visiblePlans.length === 1 ? ' mp-grid--one' : ''}${visiblePlans.length === 2 ? ' mp-grid--two' : ''}`}
            >
              {visiblePlans.map((plan, index) => {
                const price =
                  billing === 'monthly' ? plan.monthly_amount : plan.annual_amount;
                const periodLabel = billing === 'monthly' ? '/mo' : '/yr';
                const billedHint =
                  billing === 'monthly'
                    ? 'Billed monthly'
                    : `Billed annually · ~${formatMembershipMoney(plan.annual_amount / 12, plan.currency)}/mo`;

                return (
                  <article
                    key={plan.plan_id}
                    className={`mp-card mp-card--${plan.theme}${plan.featured ? ' is-featured' : ''}`}
                    style={{ animationDelay: `${120 + index * 90}ms` }}
                    onClick={() => setSelected(plan)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelected(plan);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${plan.name} plan details`}
                  >
                    <div className="mp-popular mp-popular--upgrade">
                      {renewMode || isExpired ? 'Renew' : 'Upgrade'}
                    </div>
                    <PlanIcon theme={plan.theme} />
                    <div className="mp-card-top">
                      <h2 className="mp-plan-name">{plan.name}</h2>
                      <p className="mp-plan-sub">{plan.subtitle}</p>
                    </div>
                    <div className="mp-price-block">
                      <div className="mp-price-row">
                        <span className="mp-price">
                          {formatMembershipMoney(price, plan.currency)}
                        </span>
                        <span className="mp-period">{periodLabel}</span>
                      </div>
                      <p className="mp-billed">{billedHint}</p>
                    </div>
                    {plan.includes_previous_tier && plan.previous_tier_banner ? (
                      <div className="mp-banner">{plan.previous_tier_banner}</div>
                    ) : (
                      <div className="mp-banner mp-banner--muted">What’s included</div>
                    )}
                    <ul className="mp-perks">
                      {[...plan.perks]
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((perk) => (
                          <li key={perk.perk_id}>
                            <span className="mp-check" aria-hidden>
                              <svg viewBox="0 0 16 16" width="12" height="12">
                                <path
                                  d="M3.5 8.2l2.8 2.8 6.2-6.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span>{perk.label}</span>
                          </li>
                        ))}
                    </ul>
                    <p className="mp-card-hint">Tap for full details</p>
                    <button
                      type="button"
                      className={`mp-cta mp-cta--${plan.theme}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        goGetStarted(plan);
                      }}
                    >
                      {renewMode || isExpired ? 'Renew' : 'Upgrade'}
                      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
                        <path
                          d="M4 12L12 4M12 4H6.5M12 4V9.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}

        <p className="mp-footnote">
          Cancel anytime · Member discounts apply on eligible service orders
        </p>
      </div>

      {selected && (
        <MembershipPlanModal
          plan={selected}
          billing={billing}
          onClose={() => setSelected(null)}
          onGetStarted={goGetStarted}
        />
      )}
    </div>
  );
};

export default MembershipUpgradePage;
